import { useQuery } from '@tanstack/react-query'
import { marked } from 'marked'
import { BookOpen, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'

import { EmptyState, Spinner } from '@/components/ui'
import { fold, loadChapters, TOPICS, type Chapter } from '@/help/topics'
import { cn } from '@/lib/utils'

/**
 * Pomocník - obsah vľavo, kapitola vpravo, nad obsahom vyhľadávanie.
 *
 * Vyhľadávanie prechádza celý text kapitol (bez ohľadu na diakritiku) a
 * v obsahu nechá len tie, kde sa výraz nachádza - kapitol je málo, hľadať
 * v nich na serveri by bolo zbytočné.
 */
export default function HelpPage() {
  const { t, i18n } = useTranslation()
  const { topic = TOPICS[0] } = useParams()
  const [query, setQuery] = useState('')

  const { data: chapters, isLoading } = useQuery({
    queryKey: ['help', i18n.language],
    queryFn: () => loadChapters(i18n.language),
    staleTime: Infinity,
  })

  const needle = fold(query.trim())
  const visible = useMemo(
    () => (chapters ?? []).filter((c) => !needle || fold(c.markdown).includes(needle)),
    [chapters, needle],
  )

  if (!(TOPICS as readonly string[]).includes(topic)) return <Navigate to="/help" replace />
  const chapter = chapters?.find((c) => c.topic === topic)

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <aside className="card shrink-0 p-3 lg:sticky lg:top-4 lg:w-64">
        <h1 className="mb-2 flex items-center gap-2 text-base font-semibold">
          <BookOpen size={18} className="text-brand-600" />
          {t('help.title')}
        </h1>
        <label className="relative mb-2 block">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            className="input w-full pl-8"
            placeholder={t('help.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <nav className="flex flex-col">
          {visible.map((c) => (
            <Link
              key={c.topic}
              to={`/help/${c.topic}`}
              className={cn(
                'rounded px-2 py-1.5 text-sm',
                c.topic === topic
                  ? 'bg-brand-50 font-medium text-brand-700'
                  : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              {c.title}
            </Link>
          ))}
          {chapters && !visible.length ? (
            <p className="px-2 py-1.5 text-sm text-slate-400">{t('global.noResults')}</p>
          ) : null}
        </nav>
      </aside>

      <article className="card min-w-0 flex-1 p-5 sm:p-7">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : chapter ? (
          <ChapterView chapter={chapter} />
        ) : (
          <EmptyState>{t('app.empty')}</EmptyState>
        )}
      </article>
    </div>
  )
}

function ChapterView({ chapter }: { chapter: Chapter }) {
  // Obsah je náš (súbory v repozitári), nie od používateľov - HTML z Markdownu
  // sa preto nesanitizuje.
  // Obrázok sa dá otvoriť v plnej veľkosti - v stĺpci textu je zmenšený.
  const html = useMemo(
    () =>
      marked
        .parse(chapter.markdown, { async: false })
        .replace(
          /<img src="([^"]+)"([^>]*)>/g,
          '<a href="$1" target="_blank" rel="noreferrer"><img src="$1" loading="lazy"$2></a>',
        ),
    [chapter],
  )

  return (
    <div
      className="help-prose"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
